import {
  BaseMutationOptions,
  useApolloClient,
  useMutation as useApolloMutation
} from "@apollo/client/react"
import { useTranslation } from "react-i18next"

import type { ApolloError } from "@apollo/client/core"

import { callMutation } from "shared/api/callMutation"
import {
  DuplicateEntityDocument,
  DuplicateEntityMutation,
  DuplicateEntityMutationVariables
} from "shared/api/duplicate/mutations.generated"
import { evict } from "shared/api/evict"
import {
  evictAffectedPrices,
  evictProjectPrice
} from "shared/api/price/evictPriceCache"
import { invalidateAffectedPrices } from "shared/api/price/modifyPriceCache"
import { useProcess } from "shared/api/process/useProcess"
import { MutationSchema } from "shared/api/types"
import { Logger } from "shared/lib/Logger"
import { emitAppEvent } from "shared/lib/appEventEmitter"
import { generateUUID } from "shared/lib/generateUUID"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { FloorTypename } from "shared/types/Project"
import {
  CREATE_PROJECT_CHILD,
  DUPLICATE_FLOOR
} from "shared/types/ProjectMutations"
import {
  DuplicateEntityInput,
  Process
} from "shared/types/__generated__/graphql"
import { DuplicateEntityInputSchema } from "shared/types/__generated__/validation"

import { useFloorLazyQuery } from "entities/floors/graphql/queries.generated"
import { evictProjectChildAndSort } from "entities/project-children/lib/evictProjectChildAndSort"
import { insertProjectChild } from "entities/project-children/lib/insertProjectChild"
import { useProjectChildrenLazyQuery } from "entities/projects/graphql/queries.generated"
import { readProjectFragment } from "entities/projects/lib/readProjectFragment"
import { useDuplicationAffix } from "entities/projects/model/useDuplicationAffix"
import { useProjectStoreActions } from "features/project-management/model/store/useProjectStoreActions"
import z from "zod"

import { readFloorCompleteFragment } from "../lib/readFloorFragment"

const DuplicateEntityVariablesSchema = z.object({
  input: DuplicateEntityInputSchema()
})

function stringifyReason(reason: unknown) {
  if (typeof reason === "string") return reason
  try {
    return JSON.stringify(reason) ?? String(reason)
  } catch {
    return String(reason)
  }
}

export function useDuplicateFloor() {
  const { t } = useTranslation()
  const { cache: apolloCache } = useApolloClient()
  const { setElementPending, unsetElementPending } = useProjectStoreActions()
  const [fetchProject] = useProjectChildrenLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })
  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.Floor.DUPLICATE_FLOOR.Start"),
      success: t("Request.Floor.DUPLICATE_FLOOR.Success"),
      failure: t("Request.Floor.DUPLICATE_FLOOR.Failure")
    }
  })
  const startProjectRequest = useProjectRequestMonitor()

  const handleError = useEvent(
    (
      error: ApolloError,
      options:
        | BaseMutationOptions<
            DuplicateEntityMutation,
            Partial<DuplicateEntityMutationVariables>
          >
        | undefined
    ) => {
      if (options?.variables?.input?.desiredId) {
        evict({
          cache: apolloCache,
          id: options.variables.input.desiredId,
          typename: FloorTypename
        })
      }
    }
  )

  const [apolloMutate, rest] = useApolloMutation<
    DuplicateEntityMutation,
    DuplicateEntityMutationVariables
  >(DuplicateEntityDocument)

  const [fetchFloor] = useFloorLazyQuery()
  const [fetchChildren] = useProjectChildrenLazyQuery()

  const { addSuffix } = useDuplicationAffix()
  const handleProcess = useProcess()

  const handleProcessComplete = useEvent((process: Process) => {
    if (process.copyId) {
      return fetchFloor({
        variables: { id: process.copyId },
        fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
      })
    }
    throw new Error("Process contained no ID for Result!")
  })

  const duplicateFloor = useEvent(
    async ({
      floorID,
      projectID,
      position,
      label,
      onError
    }: {
      floorID: string
      projectID: string
      label?: string
      position?: number
      onError?: (
        error: ApolloError,
        options: BaseMutationOptions | undefined
      ) => void
    }) => {
      await fetchChildren({
        variables: { id: projectID }
      })

      const floor = readFloorCompleteFragment(floorID, apolloCache)
      if (!floor) {
        const error = new Error("Floor not found!")
        Logger.error(error)
        throw error
      }
      const parent = readProjectFragment(projectID, apolloCache)
      const resolvedParentID = parent?.id
      const resolvedParentType = parent?.__typename

      if (!resolvedParentID || !resolvedParentType) {
        const error = new Error("Parent not found!")
        Logger.error(error)
        throw error
      }

      const id = generateUUID()

      insertProjectChild({
        child: {
          ...floor,
          id,
          label: label || addSuffix(floor.label || ""),
          children: [],
          hasClarificationRequest: false
        },
        position,
        parentID: resolvedParentID,
        parentType: resolvedParentType,
        cache: apolloCache
      })

      const input: DuplicateEntityInput = {
        id: generateUUID(),
        label: label || addSuffix(floor.label || ""),
        position,
        recursive: true,
        desiredId: id,
        source: { id: floorID, type: FloorTypename },
        target: { id: resolvedParentID, type: resolvedParentType }
      }

      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()
      const completeProjectRequest = startProjectRequest({
        projectID,
        requestType: DUPLICATE_FLOOR
      })

      setElementPending({ itemID: id, projectID })
      emitAppEvent(CREATE_PROJECT_CHILD, {
        id,
        projectID,
        parentID: resolvedParentID,
        parentType: resolvedParentType,
        type: FloorTypename
      })
      invalidateAffectedPrices({
        projectID,
        itemID: floorID,
        itemType: FloorTypename,
        includeChild: false,
        cache: apolloCache
      })

      let duplicationProcess: Process | undefined
      let mutationErrors: unknown

      try {
        const mutationResult = await callMutation(apolloMutate, {
          variables: { input },
          transform: data => data.process,
          schemas: {
            input: DuplicateEntityVariablesSchema,
            response: MutationSchema.extend({ process: z.custom<Process>() })
          },
          apolloOptions: {
            onError: (error, options) => {
              handleError(error, options)
              onError?.(error, options)
            }
          }
        })

        const process = mutationResult.data
        if (!process) {
          // callMutation resolves with the errors instead of throwing them
          mutationErrors = mutationResult.errors
          throw new Error("Duplication returned no Process!")
        }

        duplicationProcess = process

        const processResult = await handleProcess({
          process,
          onComplete: handleProcessComplete
        })

        raiseSuccessToast()
        evictAffectedPrices({
          projectID,
          itemID: floorID,
          itemType: FloorTypename,
          cache: apolloCache
        })

        return processResult
      } catch (e) {
        // A failed process rejects with its bare error payload (often a plain
        // string/object), which Logger would otherwise drop or report as
        // "Unknown Error", so wrap it and attach the process for diagnosis.
        Logger.error(
          e instanceof Error
            ? e
            : new Error(`Duplication failed: ${stringifyReason(e)}`),
          "Failed to duplicate floor",
          {
            projectID,
            floorID,
            process: duplicationProcess,
            mutationErrors,
            reason: e
          }
        )
        evictProjectPrice(projectID, apolloCache, true)
        raiseErrorToast()

        evictProjectChildAndSort({
          id,
          typename: FloorTypename,
          parentID: resolvedParentID,
          parentType: resolvedParentType,
          cache: apolloCache
        })
        throw e
      } finally {
        completeProjectRequest()
        fetchProject({ variables: { id: projectID } })
        unsetElementPending({ itemID: id, projectID })
      }
    }
  )
  return [duplicateFloor, rest] as [typeof duplicateFloor, typeof rest]
}
